import React, { useEffect, useState, useCallback } from "react";
import { FiPlus, FiTrash2, FiSearch, FiX, FiEye } from "react-icons/fi";
import SharedLayout from "./SharedLayout";
import superAdminApi from "./utils/superAdminApi";
import InfoModal from "./modals/InfoModals";
import ConfirmModal from "./modals/ConfirmModals";

/* ---------- Modal Wrapper ---------- */
const FormModal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center"
        >
          <FiX />
        </button>
        {children}
      </div>
    </div>
  );
};

const Companies = () => {
  /* ---------------- STATE ---------------- */
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailCompany, setDetailCompany] = useState(null);

  // create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company: "",
    name: "",
    surname: "",
    email: "",
    number: "",
    password: "",
  });

  // confirm delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [target, setTarget] = useState(null);

  // info modal
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const showInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  /* ---------------- FETCH ---------------- */
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await superAdminApi.get("/admins");
      setCompanies(res.data || []);
    } catch {
      showInfo("error", "Hata", "Firmalar yüklenemedi.");
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  /* ---------------- CREATE ---------------- */
  const submitCreate = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      await superAdminApi.post("/admins", {
        company: newCompany.company.trim(),
        name: newCompany.name.trim(),
        surname: newCompany.surname.trim(),
        email: newCompany.email.trim().toLowerCase(),
        number: newCompany.number.trim(),
        password: newCompany.password,
      });

      showInfo("success", "Başarılı", "Firma oluşturuldu.");
      setCreateOpen(false);
      setNewCompany({
        company: "",
        name: "",
        surname: "",
        email: "",
        number: "",
        password: "",
      });

      fetchCompanies();
    } catch (err) {
      showInfo(
        "error",
        "Hata",
        err.response?.data?.error || "Firma oluşturulamadı."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const confirmDelete = async () => {
    if (!target) return;
    try {
      setLoading(true);
      await superAdminApi.delete(`/admins/${target.id}`);
      showInfo("success", "Silindi", "Firma silindi.");
      fetchCompanies();
    } catch {
      showInfo("error", "Hata", "Firma silinemedi.");
    } finally {
      setLoading(false);
      setConfirmOpen(false);
      setTarget(null);
    }
  };

  /* ---------------- ACTIVE TOGGLE ---------------- */
  const toggleActive = async (row) => {
    try {
      await superAdminApi.patch(`/admins/${row.id}/active`, {
        isActive: !row.isActive,
      });
      fetchCompanies();
    } catch {
      showInfo("error", "Hata", "Durum değiştirilemedi.");
    }
  };

  /* ---------------- FILTER ---------------- */
  const filtered = companies.filter((c) => {
    const q = query.toLowerCase();
    if (!q) return true;
    return (
      c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    );
  });

  return (
    <SharedLayout pageTitle="FİRMALAR">
      <div className="bg-gray-50 p-8 rounded-2xl">
        {/* TOP BAR */}
        <div className="flex justify-between items-center mb-6">
          <div className="relative max-w-sm w-full">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Firma ara..."
              className="w-full pl-10 pr-3 py-2 rounded-full border"
            />
          </div>

          <button
            onClick={() => setCreateOpen(true)}
            className="px-5 py-2 rounded-full bg-emerald-500 text-white flex items-center gap-2"
          >
            <FiPlus /> Yeni Firma
          </button>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-xl overflow-hidden shadow">
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">Firma</th>
                <th className="px-6 py-3 text-left">Email</th>
                <th className="px-6 py-3 text-left">Durum</th>
                <th className="px-6 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center">
                    Firma bulunamadı.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-4 font-medium">{row.company}</td>
                    <td className="px-6 py-4">{row.email}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleActive(row)}
                        className={`px-3 py-1 rounded text-white ${
                          row.isActive ? "bg-green-500" : "bg-gray-400"
                        }`}
                      >
                        {row.isActive ? "Aktif" : "Pasif"}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        {/* Detay */}
                        <button
                          onClick={() => setDetailCompany(row)}
                          title="Firma Detayları"
                          className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition"
                        >
                          <FiEye size={18} />
                        </button>

                        {/* Sil */}
                        <button
                          onClick={() => {
                            setTarget(row);
                            setConfirmOpen(true);
                          }}
                          title="Firmayı Sil"
                          className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE MODAL */}
      <FormModal open={createOpen} onClose={() => setCreateOpen(false)}>
        <h3 className="text-xl font-semibold mb-4 text-center">
          Yeni Firma Oluştur
        </h3>

        <form onSubmit={submitCreate} className="space-y-4">
          <input
            placeholder="Firma Adı"
            value={newCompany.company}
            onChange={(e) =>
              setNewCompany({ ...newCompany, company: e.target.value })
            }
            className="w-full border rounded-xl px-4 py-3"
            required
          />

          <div className="grid grid-cols-2 gap-3">
            <input
              placeholder="Yetkili Adı"
              value={newCompany.name}
              onChange={(e) =>
                setNewCompany({ ...newCompany, name: e.target.value })
              }
              className="w-full border rounded-xl px-4 py-3"
              required
            />
            <input
              placeholder="Yetkili Soyadı"
              value={newCompany.surname}
              onChange={(e) =>
                setNewCompany({ ...newCompany, surname: e.target.value })
              }
              className="w-full border rounded-xl px-4 py-3"
              required
            />
          </div>

          <input
            type="email"
            placeholder="Email"
            value={newCompany.email}
            onChange={(e) =>
              setNewCompany({ ...newCompany, email: e.target.value })
            }
            className="w-full border rounded-xl px-4 py-3"
            required
          />

          <input
            placeholder="Telefon"
            value={newCompany.number}
            onChange={(e) =>
              setNewCompany({ ...newCompany, number: e.target.value })
            }
            className="w-full border rounded-xl px-4 py-3"
            required
          />

          <input
            type="password"
            placeholder="Geçici Şifre"
            value={newCompany.password}
            onChange={(e) =>
              setNewCompany({ ...newCompany, password: e.target.value })
            }
            className="w-full border rounded-xl px-4 py-3"
            required
          />

          <button
            disabled={loading}
            className="w-full h-11 rounded-xl bg-emerald-500 text-white"
          >
            {loading ? "Oluşturuluyor..." : "Firma Oluştur"}
          </button>
        </form>
      </FormModal>

      {/* CONFIRM DELETE */}
      <ConfirmModal
        open={confirmOpen}
        title="Firma Sil"
        description={`"${target?.company}" firmasını silmek istiyor musunuz?`}
        confirmText="Sil"
        confirmBtnClass="bg-red-600 text-white"
        cancelBtnClass="border"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />

      {/* COMPANY DETAIL */}
      <FormModal
        open={!!detailCompany}
        onClose={() => setDetailCompany(null)}
        maxWidth="max-w-lg"
      >
        <h3 className="text-xl font-semibold mb-4">Firma Detayları</h3>

        {detailCompany && (
          <div className="space-y-2 text-sm">
            <p>
              <b>Firma:</b> {detailCompany.company}
            </p>
            <p>
              <b>Yetkili:</b> {detailCompany.name} {detailCompany.surname}
            </p>
            <p>
              <b>Email:</b> {detailCompany.email}
            </p>
            <p>
              <b>Telefon:</b> {detailCompany.number}
            </p>
            <p>
              <b>Durum:</b> {detailCompany.isActive ? "Aktif" : "Pasif"}
            </p>
            <p>
              <b>Oluşturulma:</b>{" "}
              {new Date(detailCompany.createdAt).toLocaleString("tr-TR")}
            </p>
            {detailCompany.lastLogin && (
              <p>
                <b>Son Giriş:</b>{" "}
                {new Date(detailCompany.lastLogin).toLocaleString("tr-TR")}
              </p>
            )}
          </div>
        )}
      </FormModal>

      <InfoModal {...info} onClose={closeInfo} />
    </SharedLayout>
  );
};

export default Companies;
